import axios, { CancelTokenSource } from "axios";
import { Endpoints } from "../config/endpoints";
import {
  ExtendedCustomerData,
  ICustomerData,
  ICustomerStatus,
  IExtendCustomerData,
  IExtendedSearchResponse,
  ISearchOptions,
  ISearchResponse,
} from "../models/entities/Customer";

interface ICustomerService {
  getServiceEndpoint(): string;
  getCustomersByRegions(regionId: number): Promise<IExtendCustomerData[]>;
  getCustomersById(customerId: string): Promise<IExtendCustomerData>;
  getCustomers(customerIds: string[]): Promise<IExtendCustomerData[]>;
  getStatus(): Promise<ICustomerStatus[]>;
  getSearch(
    options: ISearchOptions,
    cancelToken?: CancelTokenSource
  ): Promise<IExtendedSearchResponse>;
}

const TIMEOUT_REQUEST = 30000;

// TODO: Update according to real service query params syntax

export const CustomerService: ICustomerService = {
  getServiceEndpoint: (): string => {
    return Endpoints.getEndpoint("REACT_APP_CUSTOMER_ENDPOINT");
  },

  getCustomersByRegions: (regionId: number): Promise<IExtendCustomerData[]> => {
    const customerServiceUrl = CustomerService.getServiceEndpoint();
    return new Promise(async (resolve, reject) => {
      const { data } = await axios.get<ICustomerData[]>(
        `${customerServiceUrl}regions/${regionId}/customers`
      );
      resolve(data.map((customer) => new ExtendedCustomerData(customer)));
    });
  },
  getCustomersById: (customerId: string): Promise<IExtendCustomerData> => {
    const customerServiceUrl = CustomerService.getServiceEndpoint();
    return new Promise(async (resolve, reject) => {
      const response = await axios.get<ICustomerData>(
        `${customerServiceUrl}customer/${customerId}`
      );
      resolve(new ExtendedCustomerData(response.data));
    });
  },
  getCustomers: async (
    customerIds: string[]
  ): Promise<IExtendCustomerData[]> => {
    const customerServiceUrl = CustomerService.getServiceEndpoint();
    const uniqueCustomerIds = Array.from(new Set(customerIds.filter(Boolean)));

    if (uniqueCustomerIds.length === 0) {
      return Promise.resolve([]);
    }

    const customerIdsJoined = uniqueCustomerIds.join("-");
    const { data } = await axios.get<ICustomerData[]>(
      `${customerServiceUrl}customers?customerIds=${customerIdsJoined}`
    );
    return Promise.resolve(
      data.map((customer) => new ExtendedCustomerData(customer))
    );
  },
  getStatus: async (): Promise<ICustomerStatus[]> => {
    const customerServiceUrl = CustomerService.getServiceEndpoint();
    const url = `${customerServiceUrl}customers/statuses`;
    const { data } = await axios.get(url);
    return Promise.resolve(data);
  },
  getSearch: async (
    options: ISearchOptions,
    cancelToken?: CancelTokenSource
  ): Promise<IExtendedSearchResponse> => {
    const customerServiceUrl = CustomerService.getServiceEndpoint();
    const result = await axios.post<ISearchResponse>(
      `${customerServiceUrl}customers/search`,
      options,
      {
        headers: { "Cache-Control": "min-fresh=36000" },
        timeout: TIMEOUT_REQUEST,
        cancelToken: cancelToken?.token,
      }
    );

    const response: IExtendedSearchResponse = {
      hasNextPage: result.data.hasNextPage,
      offset: result.data.offset,
      limit: result.data.limit,
      rows: result.data.rows,
      params: result.data.params,
      data: result.data.data.map((d) => new ExtendedCustomerData(d)),
    };

    return response;
  },
} as ICustomerService;
